/**
 * @fileoverview Componente para mostrar productos de una línea específica
 * @author Jonatan Bianchi - Bianchi Desarrollo Web
 */

import { Component, OnInit, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../components/header.component';
import { FooterComponent } from '../components/footer.component';
import { ProductosService } from '../services/productos.service';
import { Linea } from '../models/catalog.model';

@Component({
  selector: 'app-linea',
  standalone: true,
  imports: [RouterLink, HeaderComponent, FooterComponent],
  templateUrl: './linea.component.html',
  styleUrl: './linea.component.css'
})
export class LineaComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private productosService = inject(ProductosService);
  private platformId = inject(PLATFORM_ID);
  private routerSubscription?: Subscription;

  protected linea: Linea | undefined;
  protected productos: any[] = [];
  protected loading = true;
  protected currentYear = new Date().getFullYear();

  ngOnInit(): void {
    // Scroll al top cuando cambia la ruta
    this.scrollToTop();
    
    // Suscribirse a cambios de navegación para hacer scroll al top
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.scrollToTop();
      });

    this.route.paramMap.subscribe(params => {
      const lineaSlug = params.get('lineaSlug');
      if (lineaSlug) {
        this.loadLinea(lineaSlug);
      } else {
        this.router.navigate(['/']);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Hace scroll al top de la página
   */
  private scrollToTop(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  private loadLinea(lineaSlug: string): void {
    this.loading = true;
    
    // Esperar a que los productos se carguen
    if (!this.productosService.isLoaded()) {
      setTimeout(() => this.loadLinea(lineaSlug), 100);
      return;
    }

    this.linea = this.productosService.getLineaBySlug(lineaSlug);
    
    if (!this.linea) {
      // Línea no encontrada, redirigir al inicio
      this.router.navigate(['/']);
      return;
    }

    this.productos = this.productosService.getProductosByLinea(lineaSlug);
    this.loading = false;
  }

  protected formatCurrency(value: number): string {
    if (!value) return '$ 0';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Remueve el prefijo "Linea " del nombre de la línea
   */
  protected getNombreSinLinea(nombre: string): string {
    return nombre.replace(/^Linea\s+/i, '');
  }
}

